/// Run history with summary stats and infinite scroll.

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { appRoutes } from '@/app/router/routes';
import { useAuthStore } from '@/auth/handler/authStore';
import { GameRunRow } from '@/game/ui/GameRunRow';
import { DeathCauseSprite } from '@/game/ui/DeathCauseSprite';
import { useGameRunStore } from '@/game/handler/gameRunStore';
import { useIsDarkMode, useThemeColors } from '@/settings/handler/settingsStore';
import { PageChrome } from '@/ui/PageChrome';
import { PixelContainer } from '@/ui/pixel/PixelContainer';
import { PixelIcon } from '@/ui/pixel/PixelIcon';
import { formatScore } from '@/utils/formatScore';

const loadMoreThreshold = 240;

export function StatsPage() {
  const { t } = useTranslation();
  const colors = useThemeColors();
  const isDark = useIsDarkMode();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);

  const board = useGameRunStore();
  const loadInitial = useGameRunStore((s) => s.loadInitial);
  const loadMore = useGameRunStore((s) => s.loadMore);

  const [scrolledUnder, setScrolledUnder] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (user.isAnonymous) return;
    void loadInitial(false);
  }, [loadInitial, user.id, user.isAnonymous]);

  const onScroll = (): void => {
    const el = listRef.current;
    if (!el) return;
    setScrolledUnder(el.scrollTop > 0.5);
    if (!board.hasMore || board.loadingMore) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - loadMoreThreshold) {
      void loadMore();
    }
  };

  return (
    <PageChrome title={t('statsTitle')} scrolledUnder={scrolledUnder}>
      {user.isAnonymous ? (
        <StatsMessage
          message={t('recentRunsSignIn')}
          actionLabel={t('authSignIn')}
          onAction={() => navigate(appRoutes.auth)}
        />
      ) : board.loading && board.runs.length === 0 ? (
        <div className="flex flex-1 items-center justify-center">
          <span style={{ fontSize: 20, color: colors.onSurface }}>…</span>
        </div>
      ) : board.error && board.runs.length === 0 ? (
        <StatsMessage
          message={t('recentRunsError')}
          actionLabel={t('rankRetry')}
          onAction={() => void loadInitial()}
        />
      ) : board.runs.length === 0 ? (
        <StatsMessage message={t('recentRunsEmpty')} />
      ) : (
        <div
          ref={listRef}
          className="min-h-0 flex-1 overflow-y-auto px-6 pb-10 pt-4"
          onScroll={onScroll}
        >
          <div className="flex gap-2.5">
            <StatCard label={t('authHighScore')} value={formatScore(user.highScore)} />
            <StatCard label={t('statsAverageScore')} value={formatScore(board.averageScore)} />
          </div>
          <div className="mt-2.5 flex gap-2.5">
            <StatCard label={t('statsTotalGames')} value={String(board.gamesPlayed)} />
            <MostFearedStatCard
              label={t('statsMostFearedEnemy')}
              emptyLabel={t('statsMostFearedEnemyEmpty')}
              deathCause={board.mostFearedEnemy}
              isDark={isDark}
            />
          </div>
          <h2 className="mb-3 mt-6 leading-none" style={{ fontSize: 22, color: colors.onSurface }}>
            {t('recentRunsSection')}
          </h2>
          <div className="flex flex-col gap-2.5">
            {board.runs.map((run) => (
              <GameRunRow key={run.id} run={run} />
            ))}
          </div>
          {board.loadingMore && (
            <p className="py-4 text-center" style={{ fontSize: 18, color: colors.onSurface }}>
              …
            </p>
          )}
          {board.error && board.runs.length > 0 && (
            <p className="pt-2" style={{ fontSize: 16, color: colors.onSurface, opacity: 0.75 }}>
              {board.error}
            </p>
          )}
        </div>
      )}
    </PageChrome>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  const colors = useThemeColors();
  return (
    <PixelContainer
      radius={3}
      pixelScale={2}
      padding="12px"
      backgroundColor={colors.surfaceContainerHighest}
      borderColor={colors.onSurface}
      style={{ flex: 1, minWidth: 0 }}
    >
      <div
        style={{
          height: statCardLabelSlotHeight,
          fontSize: 14,
          lineHeight: 1,
          color: colors.onSurface,
          opacity: 0.65,
          overflow: 'hidden',
        }}
      >
        {label}
      </div>
      <div
        className="mt-1.5 flex items-center"
        style={{ height: statCardValueSlotHeight }}
      >
        <div className="leading-tight" style={{ fontSize: 22, color: colors.onSurface }}>
          {value}
        </div>
      </div>
    </PixelContainer>
  );
}

/** Matches MostFearedStatCard sprite slot so all four tiles share a size. */
const statCardValueSlotHeight = 32;
const statCardLabelSlotHeight = 28;

function MostFearedStatCard({
  label,
  emptyLabel,
  deathCause,
  isDark,
}: {
  label: string;
  emptyLabel: string;
  deathCause: string;
  isDark: boolean;
}) {
  const colors = useThemeColors();
  const hasEnemy = deathCause.length > 0;

  return (
    <PixelContainer
      radius={3}
      pixelScale={2}
      padding="12px"
      backgroundColor={colors.surfaceContainerHighest}
      borderColor={colors.onSurface}
      style={{ flex: 1, minWidth: 0 }}
    >
      <div
        style={{
          height: statCardLabelSlotHeight,
          fontSize: 14,
          lineHeight: 1,
          color: colors.onSurface,
          opacity: 0.65,
          overflow: 'hidden',
        }}
      >
        {label}
      </div>
      <div
        className="mt-1.5 flex items-center"
        style={{ height: statCardValueSlotHeight }}
      >
        {hasEnemy ? (
          <DeathCauseSprite
            cause={deathCause}
            isDark={isDark}
            maxWidth={40}
            maxHeight={statCardValueSlotHeight}
          />
        ) : (
          <div className="leading-tight" style={{ fontSize: 22, color: colors.onSurface }}>
            {emptyLabel}
          </div>
        )}
      </div>
    </PixelContainer>
  );
}

function StatsMessage({
  message,
  actionLabel,
  onAction,
}: {
  message: string;
  actionLabel?: string;
  onAction?: () => void;
}) {
  const colors = useThemeColors();
  return (
    <div className="flex flex-1 flex-col items-center justify-center px-8 py-12 text-center">
      <PixelIcon name="chart" set="pixel_icons" size={40} color={colors.onSurface} style={{ opacity: 0.5 }} />
      <p className="mt-4 leading-tight" style={{ fontSize: 20, color: colors.onSurface, opacity: 0.75 }}>
        {message}
      </p>
      {actionLabel && onAction && (
        <button
          type="button"
          className="mt-4 underline"
          style={{ fontSize: 18, color: colors.onSurface }}
          onClick={onAction}
        >
          {actionLabel}
        </button>
      )}
    </div>
  );
}
